import { AxiosInstance } from 'axios';
import axiosClient from './axiosClientConfig';
import {
	ActionStatus,
	BulkFlagClient,
	CaseApplicationQuestion,
	CaseApplications,
	ClientCancelStatusResponse,
	ClientStep,
	EligibleState,
	FlagReason,
	FlagReasonsCountRequest,
	FlagReasonWithCount,
	FollowUpList,
	HttpResponse,
	MessageFilterType,
	RejectClient,
	SegmentationFilterListData,
} from '@/interfaces';
import {
	CaseClientsRequest,
	CaseClientsResponse,
	ExportCsvRequest,
} from '@/interfaces/lawyerDirectory';

class LawyerDirectory {
	constructor(private client: AxiosInstance) {}

	getCaseClients(
		props: CaseClientsRequest
	): Promise<HttpResponse<CaseClientsResponse>> {
		const questions =
			props.questions && props.questions.length > 0
				? props.questions.map((q) => ({
						...q,
						// DATE questions carry a synthetic "from - to" string used
						// only by the UI to restore the selection. The backend only
						// needs the questionId, so we send answers: [].
						answers:
							q.answers.length === 1 && q.answers[0].includes(' - ')
								? []
								: q.answers,
				  }))
				: undefined;

		const claimFormQuestions =
			props.claimFormQuestions && props.claimFormQuestions.length > 0
				? props.claimFormQuestions.map((q) => ({
						...q,
						answers:
							q.answers.length === 1 && q.answers[0].includes(' - ')
								? []
								: q.answers,
				  }))
				: undefined;

		const flagged_reasons =
			props.flagged_reasons && props.flagged_reasons.length > 0
				? props.flagged_reasons.filter((reason) => reason.trim() !== '')
				: [];

		const flagged: boolean | undefined =
			flagged_reasons.length > 0
				? true
				: props.flagged !== undefined && props.flagged !== null
				? props.flagged
				: undefined;

		const params = {
			page: props.page,
			limit: props.limit ?? 100,
			...(props.caseId && { caseId: props.caseId }),
			...(props?.search && { search: props.search }),
			...(props.clientId && { clientId: props.clientId }),
			...(props.segmentId && { segmentId: props.segmentId }),
			...(props.totalCase && { totalCase: props.totalCase }),
			...(props.totalMessage && { totalMessage: props.totalMessage }),
			...(props.messageFilter && {
				messageFilter: props.messageFilter.toLocaleUpperCase(),
			}),
			...(props.totalTaskClient !== undefined &&
				props.totalTaskClient !== null && {
					totalTaskClient: props.totalTaskClient,
				}),
			...(props.totalTaskLawyer !== undefined &&
				props.totalTaskLawyer !== null && {
					totalTaskLawyer: props.totalTaskLawyer,
				}),
			...(props.actionStatusClient && {
				actionStatusClient: props.actionStatusClient.toLocaleUpperCase(),
			}),
			...(props.actionStatusLawyer && {
				actionStatusLawyer: props.actionStatusLawyer.toLocaleUpperCase(),
			}),
			...(props.start_date && { start_date: props.start_date }),
			...(props.end_date && { end_date: props.end_date }),
			...(props.orderBy && { orderBy: props.orderBy }),
			...(props.status && { status: props.status }),
			...(props.flagged && { flagged: props.flagged }),
			...(questions && { questions }),
			...(claimFormQuestions && { claimFormQuestions }),
			...(flagged !== undefined && { flagged }),
			...(flagged_reasons.length > 0 && { flagged_reasons }),
			...(props.documents &&
				props.documents.length > 0 && { documents: props.documents }),
		};

		const stepperStatusIds =
			props.stepperStatusIds && props.stepperStatusIds.length > 0
				? props.stepperStatusIds.map(Number)
				: [];

		const stateIds =
			props.stateIds && props.stateIds.length > 0
				? props.stateIds.map(Number)
				: [];

		return this.client.post('/lawyer-directory', {
			...params,
			stepperStatusIds,
			stateIds,
		});
	}

	getClientsLists(
		page: number,
		caseId: string
	): Promise<HttpResponse<CaseClientsResponse>> {
		const params = {
			page: page,
			limit: 100,
			caseId,
		};
		return this.client.get(`/lawyer-directory`, { params });
	}

	getActionFilters(): Promise<HttpResponse<ActionStatus[]>> {
		return this.client.get(`/lawyer-directory/action-filters`);
	}

	getTypeMessageFilters(): Promise<HttpResponse<MessageFilterType[]>> {
		return this.client.get(`/lawyer-directory/message-filter`);
	}

	getSegmentFilters(
		page: number
	): Promise<HttpResponse<SegmentationFilterListData>> {
		return this.client.get(
			`/lawyer-directory/segmentation-list?page=${page}&limit=${50}`
		);
	}

	getCaseDetailsByUser(
		userId: string,
		caseId: string
	): Promise<HttpResponse<CaseApplications>> {
		return this.client.get(`/lawyer-directory/${userId}/${caseId}`);
	}

	async applicantApproval({
		id,
		isApproved,
	}: {
		id: number;
		isApproved: boolean;
	}) {
		return this.client.post(`/applications-lawyers/${id}/approve-reject-v2`, {
			applicationId: id,
			isApproved,
		});
	}
	async cancelClient(id: number, isCanceled: boolean) {
		return this.client.post(`/applications-lawyers/${id}/cancel-client`, {
			applicationId: id,
			isCanceled: isCanceled,
		});
	}
	async getClientCancelStatus(
		id: number | undefined
	): Promise<HttpResponse<ClientCancelStatusResponse>> {
		return this.client.get(`/action-required/cancel-clients/${id}`);
	}
	async getClientApproveStatus(
		id: number | undefined
	): Promise<HttpResponse<any>> {
		return this.client.get(`/action-required/approve-reject/${id}`);
	}
	async getClientSteps(case_id: string): Promise<HttpResponse<ClientStep[]>> {
		return this.client.get(`/lawyer-directory/stepper-statuses/${case_id}`);
	}

	async getFollowUps(case_id: string): Promise<HttpResponse<FollowUpList[]>> {
		return this.client.get(`/lawyer-directory/follow-ups/${case_id}`);
	}

	async getClaimFormQuestions(
		case_id: string
	): Promise<HttpResponse<CaseApplicationQuestion[]>> {
		return this.client.get(`/lawyer-directory/claim-form-questions/${case_id}`);
	}

	async getDocumentsList(
		case_id: string
	): Promise<HttpResponse<{ name: string }[]>> {
		return this.client.get(`/lawyer-directory/documents/${case_id}`);
	}

	async getEligibleStates(
		case_id: string
	): Promise<HttpResponse<EligibleState[]>> {
		return this.client.get(`/lawyer-directory/eligible-states/${case_id}`);
	}

	async postFlagClient(
		applicationId: number,
		reasons: string[]
	): Promise<HttpResponse<{ message: string }>> {
		return this.client.post(`/lawyer-directory/flag/${applicationId}`, {
			reasons,
		});
	}

	async deleteAllFlags(
		applicationId: number
	): Promise<HttpResponse<{ message: string }>> {
		return this.client.delete(`/lawyer-directory/flag/${applicationId}`);
	}

	async deleteFlagReasons(
		applicationId: number,
		reasons: string[]
	): Promise<HttpResponse<{ message: string }>> {
		return this.client.delete(
			`/lawyer-directory/flag/${applicationId}/reasons`,
			{ data: { reasons } }
		);
	}

	async getFlagReasons(caseId: string): Promise<HttpResponse<FlagReason[]>> {
		return this.client.get(`/lawyer-directory/flag-reasons/${caseId}`);
	}

	async getFlagReasonsCount(
		data: FlagReasonsCountRequest
	): Promise<HttpResponse<FlagReasonWithCount[]>> {
		return this.client.post(`/lawyer-directory/flag-reasons/count`, data);
	}

	async postFlagReason(
		caseId: string,
		name: string,
		description?: string
	): Promise<HttpResponse<FlagReason>> {
		return this.client.post(`/lawyer-directory/flag-reasons/${caseId}`, {
			name,
			description,
		});
	}

	async putFlagReason(
		flagReasonId: number,
		name: string,
		description?: string
	): Promise<HttpResponse<{ message: string }>> {
		return this.client.put(`/lawyer-directory/flag-reasons/${flagReasonId}`, {
			name,
			description,
		});
	}

	async deleteFlagReason(
		flagReasonId: number
	): Promise<HttpResponse<{ message: string }>> {
		return this.client.delete(`/lawyer-directory/flag-reasons/${flagReasonId}`);
	}

	async exportCsv(params: ExportCsvRequest): Promise<any> {
		return this.client.post('/lawyer-directory/export', params, {
			responseType: 'arraybuffer',
		});
	}

	async postRejectClient(
		data: RejectClient
	): Promise<HttpResponse<{ message: string }>> {
		return this.client.post(`/lawyer-directory/reject`, data);
	}

	async postBulkFlagClient(
		data: BulkFlagClient
	): Promise<HttpResponse<{ message: string }>> {
		return this.client.post(`/lawyer-directory/flag-multiple`, data);
	}
}

export const lawyerDirectoryService = new LawyerDirectory(axiosClient);
